import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, CardHeader, CardBody, Button, Table, Input, Badge, Row, Col, Label,
    Progress, Spinner
} from 'reactstrap';
import Toastify from 'toastify-js';
import 'toastify-js/src/toastify.css';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import DownloadFileService from 'services/fileManager.service';
import settingService from 'services/setting.service';

// ═══════ AI Models Categories ═══════
const AI_CATEGORIES = [
    {
        title: 'Translate',
        icon: 'fas fa-language',
        color: '#3498db',
        keys: [
            { key: 'aiModels_openai_translate', label: 'OpenAI' },
            { key: 'aiModels_grok_translate', label: 'Grok' },
            { key: 'aiModels_perplexity_translate', label: 'Perplexity' },
            { key: 'aiModels_yescale_translate', label: 'YEScale' },
            { key: 'aiModels_deepseek_translate', label: 'DeepSeek' },
        ]
    },
    {
        title: 'Content Generation',
        icon: 'fas fa-pen-fancy',
        color: '#9b59b6',
        keys: [
            { key: 'aiModels_openai_prompt', label: 'OpenAI' },
            { key: 'aiModels_gemini_prompt', label: 'Google Gemini' },
            { key: 'aiModels_grok_prompt', label: 'Grok' },
            { key: 'aiModels_perplexity_prompt', label: 'Perplexity' },
            { key: 'aiModels_deepseek_prompt', label: 'DeepSeek' },
        ]
    },
    {
        title: 'Speech & Image',
        icon: 'fas fa-microphone-alt',
        color: '#e67e22',
        keys: [
            { key: 'aiModels_openai_speech', label: 'OpenAI Speech/TTS' },
            { key: 'aiModels_openai_image', label: 'OpenAI Image' },
            { key: 'aiModels_openai_whisper', label: 'OpenAI Whisper' },
        ]
    },
    {
        title: 'Ollama (Local AI)',
        icon: 'fas fa-server',
        color: '#1abc9c',
        keys: [
            { key: 'aiModels_ollama_translate', label: 'Ollama Translate', hasSize: true },
        ]
    },
    {
        title: 'Speech (Coqui XTTS)',
        icon: 'fas fa-comment-dots',
        color: '#e74c3c',
        keys: [
            { key: 'aiModels_coqui_xtts', label: 'Coqui XTTS Repos', isRepo: true },
        ]
    }
];

function DHBRenderModel() {
    const [manifest, setManifest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);


    // File browser
    const [versionFiles, setVersionFiles] = useState([]);
    const [modelFiles, setModelFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});

    // AI Models state
    const [aiSettings, setAiSettings] = useState({});
    const [aiSaving, setAiSaving] = useState({});
    const [editingKey, setEditingKey] = useState(null);
    const [editModels, setEditModels] = useState({});
    const [aiLoading, setAiLoading] = useState(true);

    const formatSize = (size) => {
        if (!size) return '—';
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };

    const showToast = (text, bg) => {
        Toastify({ text, backgroundColor: bg, close: true, position: 'right', gravity: 'bottom' }).showToast();
    };

    // ═══════ Manifest ═══════
    const loadManifest = useCallback(async () => {
        setLoading(true);
        try {
            const res = await DownloadFileService.getWhisperManifest();
            setManifest(res.data);
        } catch {
            setManifest({ latestVersion: '', models: [], versions: [] });
        } finally {
            setLoading(false);
        }
    }, []);

    const saveManifest = async (updatedManifest) => {
        setSaving(true);
        try {
            await DownloadFileService.updateWhisperManifest(updatedManifest);
            setManifest(updatedManifest);
            showToast('Lưu manifest thành công', '#4caf50');
        } catch (err) {
            showToast(`Lỗi lưu manifest: ${err}`, '#f44336');
        } finally {
            setSaving(false);
        }
    };

    // ═══════ File loading ═══════
    const loadEngineFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/Whisper');
            setVersionFiles(res.data || []);
        } catch {
            setVersionFiles([]);
        }
    }, []);

    const loadModelFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/Whisper/Models');
            setModelFiles(res.data || []);
        } catch {
            setModelFiles([]);
        }
    }, []);

    useEffect(() => { loadManifest(); loadAiSettings(); }, [loadManifest]);

    useEffect(() => {
        loadEngineFiles();
        loadModelFiles();
    }, [manifest, loadEngineFiles, loadModelFiles]);

    // ═══════ Version Actions ═══════
    const handleSetLatest = (version) => {
        saveManifest({ ...manifest, latestVersion: version });
    };

    const showAddVersionDialog = () => {
        let selectedFile = null;
        Swal.fire({
            title: 'Thêm phiên bản Engine',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version (vd: v2)</label>
                    <input id="swal-version" class="swal2-input" placeholder="v2" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Engine (.zip)</label>
                    <input id="swal-engine-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-file-info" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label>
                    <input id="swal-date" type="date" class="swal2-input" value="${new Date().toISOString().split('T')[0]}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label>
                    <textarea id="swal-changelog" class="swal2-textarea" placeholder="Mô tả thay đổi..." style="margin:0;width:100%"></textarea>
                    <div id="swal-progress" style="margin-top:10px;display:none">
                        <small id="swal-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-progress-bar" style="background:#6366f1;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-engine-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) {
                        document.getElementById('swal-file-info').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                    }
                });
            },
            preConfirm: async () => {
                const version = document.getElementById('swal-version').value.trim();
                const releaseDate = document.getElementById('swal-date').value;
                const changelog = document.getElementById('swal-changelog').value.trim();

                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file engine');

                // Show progress
                const progressDiv = document.getElementById('swal-progress');
                const progressBar = document.getElementById('swal-progress-bar');
                const progressText = document.getElementById('swal-progress-text');
                progressDiv.style.display = 'block';
                Swal.showLoading();

                try {
                    await DownloadFileService.uploadFileChunked('Plugin/Whisper', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        progressBar.style.width = pct + '%';
                        progressText.textContent = `Đang tải lên... ${pct}%`;
                    });
                    return { version, engineFile: selectedFile.name, releaseDate, changelog };
                } catch (err) {
                    Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const v = result.value;
                // Xóa version cũ nếu trùng, rồi thêm version mới lên đầu
                const filteredVersions = (manifest.versions || []).filter(ver => ver.version !== v.version);
                const updated = {
                    ...manifest,
                    latestVersion: v.version,
                    versions: [{ version: v.version, engineFile: v.engineFile, releaseDate: v.releaseDate, changelog: v.changelog }, ...filteredVersions]
                };
                saveManifest(updated);
                loadEngineFiles();
            }
        });
    };

    const handleAddVersion = showAddVersionDialog;

    const handleDeleteVersion = (idx) => {
        Swal.fire({
            title: 'Xóa phiên bản?',
            text: `Xóa "${manifest.versions[idx].version}" khỏi manifest? (Không xóa file trên server)`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                saveManifest({ ...manifest, versions: manifest.versions.filter((_, i) => i !== idx) });
            }
        });
    };

    const showAddModelDialog = () => {
        let selectedFile = null;
        Swal.fire({
            title: 'Thêm Model Weight',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tên hiển thị (vd: Medium)</label>
                    <input id="swal-model-name" class="swal2-input" placeholder="Medium" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Model (.zip)</label>
                    <input id="swal-model-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-mfile-info" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <div id="swal-mprogress" style="margin-top:10px;display:none">
                        <small id="swal-mprogress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-mprogress-bar" style="background:#f59e0b;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-model-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) {
                        document.getElementById('swal-mfile-info').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                    }
                });
            },
            preConfirm: async () => {
                const name = document.getElementById('swal-model-name').value.trim();

                if (!name) return Swal.showValidationMessage('Vui lòng nhập tên model');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file model');

                const progressDiv = document.getElementById('swal-mprogress');
                const progressBar = document.getElementById('swal-mprogress-bar');
                const progressText = document.getElementById('swal-mprogress-text');
                progressDiv.style.display = 'block';
                Swal.showLoading();

                try {
                    await DownloadFileService.uploadFileChunked('Plugin/Whisper/Models', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        progressBar.style.width = pct + '%';
                        progressText.textContent = `Đang tải lên... ${pct}%`;
                    });
                    return { name, file: selectedFile.name, size: selectedFile.size };
                } catch (err) {
                    Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const m = result.value;
                const updated = {
                    ...manifest,
                    models: [...(manifest.models || []), { name: m.name, file: m.file, size: m.size }]
                };
                saveManifest(updated);
                loadModelFiles();
            }
        });
    };

    const handleAddSharedModel = showAddModelDialog;

    const handleDeleteSharedModel = (mIdx) => {
        const model = manifest.models[mIdx];
        Swal.fire({
            title: `Xóa model "${model.name}"?`,
            text: 'Chỉ xóa khỏi manifest, không xóa file.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                saveManifest({ ...manifest, models: manifest.models.filter((_, i) => i !== mIdx) });
            }
        });
    };

    // ═══════ File Upload/Delete ═══════
    const handleFileUpload = (folder, event, onComplete) => {
        const filesToUpload = event.target.files;
        if (!filesToUpload.length) return;

        Array.from(filesToUpload).forEach(file => {
            const progressKey = `${folder}/${file.name}`;
            DownloadFileService.uploadFileChunked(folder, file, (progressEvent) => {
                const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                setUploadProgress(prev => ({ ...prev, [progressKey]: progress }));
            })
                .then(() => {
                    setUploadProgress(prev => {
                        const next = { ...prev };
                        delete next[progressKey];
                        return next;
                    });
                    showToast(`${file.name} đã tải lên thành công`, '#4caf50');
                    if (onComplete) onComplete();
                })
                .catch(() => {
                    showToast(`Lỗi tải lên ${file.name}`, '#f44336');
                });
        });
    };

    const handleDeleteFile = (folder, fileName, onComplete) => {
        Swal.fire({
            title: 'Xóa file?',
            text: `Xóa "${fileName}" khỏi server?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                DownloadFileService.deleteFiles(folder, [fileName])
                    .then(() => {
                        showToast('Đã xóa file', '#4caf50');
                        if (onComplete) onComplete();
                    })
                    .catch(() => showToast('Lỗi xóa file', '#f44336'));
            }
        });
    };

    // ═══════ AI Models ═══════
    const loadAiSettings = async () => {
        setAiLoading(true);
        try {
            const res = await settingService.getAll();
            if (res.data?.success) {
                const map = {};
                res.data.settings
                    .filter(s => s.key.startsWith('aiModels_'))
                    .forEach(s => { map[s.key] = s.value; });
                setAiSettings(map);
            }
        } catch (err) {
            console.error('Lỗi lấy AI settings:', err);
        } finally {
            setAiLoading(false);
        }
    };

    const startEdit = useCallback((key) => {
        const models = aiSettings[key] || [];
        setEditModels(JSON.parse(JSON.stringify(models)));
        setEditingKey(key);
    }, [aiSettings]);

    const cancelEdit = () => { setEditingKey(null); setEditModels([]); };

    const saveAiEdit = async () => {
        if (!editingKey) return;
        const cleaned = editModels.filter(m => m.name?.trim() && (m.model?.trim() || m.repo?.trim()));
        setAiSaving(prev => ({ ...prev, [editingKey]: true }));
        try {
            const res = await settingService.update(editingKey, cleaned);
            if (res.data?.success) {
                setAiSettings(prev => ({ ...prev, [editingKey]: cleaned }));
                setEditingKey(null);
                setEditModels([]);
                showToast('Đã lưu AI model', '#4caf50');
            }
        } catch {
            showToast('Lỗi lưu AI model', '#f44336');
        } finally {
            setAiSaving(prev => ({ ...prev, [editingKey]: false }));
        }
    };

    const addAiModel = () => setEditModels(prev => [...prev, { name: '', model: '' }]);
    const removeAiModel = (idx) => setEditModels(prev => prev.filter((_, i) => i !== idx));
    const updateAiModel = (idx, field, value) => setEditModels(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
    const moveAiModel = (idx, direction) => {
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= editModels.length) return;
        setEditModels(prev => {
            const arr = [...prev];
            [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
            return arr;
        });
    };

    // ═══════ File Browser Component ═══════
    const FileBrowser = ({ folder, files, onRefresh, label }) => (
        <div style={{ borderTop: '1px solid #e2e8f0', padding: '12px 20px', backgroundColor: '#f8fafc' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <strong style={{ fontSize: 13, color: '#64748b' }}>
                    <i className="fas fa-folder" style={{ marginRight: 6 }} />
                    {label || folder + '/'}
                </strong>
                <label style={{ cursor: 'pointer', margin: 0 }}>
                    <input type="file" multiple style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(folder, e, onRefresh)} />
                    <Button color="info" size="sm" tag="span">
                        <i className="fas fa-upload" style={{ marginRight: 4 }} /> Upload
                    </Button>
                </label>
            </div>
            {Object.entries(uploadProgress)
                .filter(([key]) => key.startsWith(folder + '/'))
                .map(([key, progress]) => (
                    <div key={key} style={{ marginBottom: 6 }}>
                        <small>{key.split('/').pop()}</small>
                        <Progress value={progress} style={{ height: 6 }} />
                    </div>
                ))}
            {(files || []).filter(f => f.type === 'file').map(file => (
                <div key={file.name} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13
                }}>
                    <span style={{ fontFamily: 'monospace' }}>
                        <i className="fas fa-file-archive" style={{ marginRight: 6, color: '#6366f1' }} />
                        {file.name}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ color: '#94a3b8' }}>{formatSize(file.size)}</span>
                        <Button color="link" size="sm" style={{ color: '#ef4444', padding: 0 }}
                            onClick={() => handleDeleteFile(folder, file.name, onRefresh)}>
                            <i className="fas fa-trash" />
                        </Button>
                    </div>
                </div>
            ))}
            {(!files || files.filter(f => f.type === 'file').length === 0) && (
                <div style={{ color: '#94a3b8', fontSize: 13, padding: '8px 0' }}>Chưa có file nào</div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="content" style={{ display: 'flex', justifyContent: 'center', paddingTop: 100 }}>
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <div className="content">
            {/* ═══════ Whisper Engine Card ═══════ */}
            <Card style={{ borderRadius: 15, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none', marginBottom: 20 }}>
                <CardHeader style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    padding: '16px 24px',
                    borderRadius: '15px 15px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                        <h4 style={{ color: '#fff', margin: 0, fontWeight: 700 }}>
                            <i className="fas fa-brain" style={{ marginRight: 10 }} />
                            Whisper Engine
                        </h4>
                        <Badge color="success" style={{ fontSize: 13, padding: '5px 10px' }}>
                            {manifest?.latestVersion || '—'}
                        </Badge>
                        {saving && <Spinner size="sm" style={{ color: '#fff' }} />}
                    </div>
                </CardHeader>
                <CardBody style={{ padding: '20px 24px' }}>
                    <Row>
                        {/* ── Left: Versions ── */}
                        <Col md="6">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                <div>
                                    <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <i className="fas fa-code-branch" style={{ color: '#6366f1' }} />
                                        Phiên bản Engine
                                    </h6>
                                    <small style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>
                                        <i className="fas fa-folder" style={{ marginRight: 4 }} />Plugin/Whisper/
                                    </small>
                                </div>
                                <Button color="primary" size="sm" onClick={showAddVersionDialog}>
                                    <i className="fas fa-plus" style={{ marginRight: 5 }} /> Thêm
                                </Button>
                            </div>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                                <Table hover responsive size="sm" style={{ margin: 0 }}>
                                    <thead style={{ backgroundColor: '#f8fafc' }}>
                                        <tr>
                                            <th style={{ padding: '8px 14px' }}>Version</th>
                                            <th>Engine File</th>
                                            <th>Size</th>
                                            <th style={{ textAlign: 'center' }}>File</th>
                                            <th>Ngày</th>
                                            <th style={{ width: 90, textAlign: 'center' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(manifest?.versions || []).map((ver, vIdx) => (
                                            <tr key={ver.version}>
                                                <td style={{ padding: '6px 14px', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                                                    {ver.version}
                                                    {ver.version === manifest.latestVersion && (
                                                        <Badge color="success" style={{ marginLeft: 6, fontSize: 9 }}>Latest</Badge>
                                                    )}
                                                </td>
                                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{ver.engineFile}</td>
                                                <td style={{ fontSize: 12 }}>{formatSize(versionFiles.find(f => f.name === ver.engineFile)?.size)}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <Badge color={ver.exist ? 'success' : 'danger'} style={{ fontSize: 10 }}>
                                                        {ver.exist ? '✅' : '❌'}
                                                    </Badge>
                                                </td>
                                                <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{ver.releaseDate || '—'}</td>
                                                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    {ver.version !== manifest.latestVersion && (
                                                        <Button color="link" size="sm" style={{ padding: '0 4px', color: '#f59e0b', fontSize: 11 }}
                                                            onClick={() => handleSetLatest(ver.version)} title="Đặt làm Latest">
                                                            <i className="fas fa-star" />
                                                        </Button>
                                                    )}
                                                    <Button color="link" size="sm" style={{ padding: '0 4px', color: '#ef4444', fontSize: 11 }}
                                                        onClick={() => handleDeleteVersion(vIdx)} title="Xóa">
                                                        <i className="fas fa-trash" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {(!manifest?.versions || manifest.versions.length === 0) && (
                                            <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: 16, fontSize: 13 }}>Chưa có phiên bản</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>

                        {/* ── Right: Shared Models ── */}
                        <Col md="6">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                <div>
                                    <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <i className="fas fa-cubes" style={{ color: '#f59e0b' }} />
                                        Model Weights (dùng chung)
                                    </h6>
                                    <small style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>
                                        <i className="fas fa-folder" style={{ marginRight: 4 }} />Plugin/Whisper/Models/
                                    </small>
                                </div>
                                <Button color="warning" size="sm" onClick={showAddModelDialog}>
                                    <i className="fas fa-plus" style={{ marginRight: 5 }} /> Thêm
                                </Button>
                            </div>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                                <Table hover responsive size="sm" style={{ margin: 0 }}>
                                    <thead style={{ backgroundColor: '#f8fafc' }}>
                                        <tr>
                                            <th style={{ padding: '8px 14px' }}>Model</th>
                                            <th>File</th>
                                            <th>Size</th>
                                            <th style={{ textAlign: 'center' }}>File</th>
                                            <th style={{ width: 50, textAlign: 'center' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(manifest?.models || []).map((model, mIdx) => (
                                            <tr key={mIdx}>
                                                <td style={{ padding: '6px 14px', fontWeight: 600, fontSize: 13 }}>{model.name}</td>
                                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{model.file}</td>
                                                <td style={{ fontSize: 12 }}>{formatSize(model.size)}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <Badge color={model.exist ? 'success' : 'danger'} style={{ fontSize: 10 }}>
                                                        {model.exist ? '✅' : '❌'}
                                                    </Badge>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <Button color="link" style={{ color: '#ef4444', padding: 0 }}
                                                        onClick={() => handleDeleteSharedModel(mIdx)}>
                                                        <i className="fas fa-times" style={{ fontSize: 12 }} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {(!manifest?.models || manifest.models.length === 0) && (
                                            <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 16, fontSize: 13 }}>Chưa có model</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>


            {/* ═══════ AI Models Card ═══════ */}
            <style>{`
                .ai-model-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 5px 12px;
                    background: #f0f2f5;
                    border-radius: 6px;
                    font-size: 13px;
                    color: #2c3e50;
                    margin: 3px 4px;
                }
                .ai-model-badge .model-id {
                    color: #8898aa;
                    font-family: monospace;
                    font-size: 12px;
                }
                .provider-card {
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    padding: 16px;
                    margin-bottom: 12px;
                    transition: all 0.2s;
                    break-inside: avoid;
                }
                .provider-card:hover { border-color: #d0d5dd; }
                .edit-row {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 6px;
                }
                .edit-row input {
                    border-radius: 8px;
                    border: 1px solid #d0d5dd;
                    padding: 5px 10px;
                    font-size: 13px;
                }
                .btn-icon-sm {
                    width: 28px;
                    height: 28px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                    border: none;
                    cursor: pointer;
                    font-size: 12px;
                    transition: all 0.15s;
                }
            `}</style>

            <Card style={{
                borderRadius: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: 'none',
                overflow: 'hidden',
                marginTop: '8px'
            }}>
                <CardHeader style={{
                    background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                    padding: '20px 24px',
                    borderBottom: 'none'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <i className="fas fa-robot" style={{
                            fontSize: '28px',
                            color: '#fff',
                            opacity: 0.9
                        }}></i>
                        <div style={{ lineHeight: '1.3' }}>
                            <Label style={{
                                fontSize: '18px',
                                color: '#fff',
                                fontWeight: '700',
                                marginBottom: '0',
                                display: 'block'
                            }}>
                                AI Models
                            </Label>
                            <span style={{
                                fontSize: '13px',
                                color: 'rgba(255,255,255,0.7)'
                            }}>
                                Quản lý danh sách mô hình AI theo từng nhà cung cấp
                            </span>
                        </div>
                    </div>
                </CardHeader>
                <CardBody style={{ padding: '20px 24px' }}>
                    {aiLoading ? (
                        <div style={{ textAlign: 'center', padding: 20 }}>
                            <Spinner size="sm" color="primary" />
                        </div>
                    ) : (
                        AI_CATEGORIES.map((cat, catIdx) => (
                            <div key={catIdx} style={{ marginBottom: catIdx < AI_CATEGORIES.length - 1 ? '24px' : 0 }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '14px',
                                    padding: '10px 16px',
                                    background: `linear-gradient(135deg, ${cat.color} 0%, ${cat.color}cc 100%)`,
                                    borderRadius: '12px',
                                }}>
                                    <i className={cat.icon} style={{ fontSize: '18px', color: '#fff', opacity: 0.9 }}></i>
                                    <Label style={{ fontSize: '15px', color: '#fff', fontWeight: '700', marginBottom: 0 }}>
                                        {cat.title}
                                    </Label>
                                    <span style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        color: '#fff',
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        fontSize: '12px'
                                    }}>
                                        {cat.keys.length} providers
                                    </span>
                                </div>
                                <div style={{ columnCount: cat.keys.some(k => k.hasSize || k.isRepo) ? 2 : 3, columnGap: 16 }}>
                                    {cat.keys.map(({ key, label, hasSize, isRepo }) => {
                                        const models = aiSettings[key] || [];
                                        const isEditing = editingKey === key;
                                        return (
                                            <div key={key}>
                                                <div className="provider-card">
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>
                                                            {label}
                                                        </span>
                                                        {!isEditing ? (
                                                            <button className="btn-icon-sm" style={{ background: '#f0f2f5', color: '#555' }}
                                                                onClick={() => startEdit(key)} title="Chỉnh sửa">
                                                                <i className="fas fa-pen"></i>
                                                            </button>
                                                        ) : (
                                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                                <button className="btn-icon-sm" style={{ background: '#2dce89', color: '#fff' }}
                                                                    onClick={saveAiEdit} disabled={aiSaving[key]} title="Lưu">
                                                                    {aiSaving[key] ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                                                                </button>
                                                                <button className="btn-icon-sm" style={{ background: '#f5365c', color: '#fff' }}
                                                                    onClick={cancelEdit} title="Hủy">
                                                                    <i className="fas fa-times"></i>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {!isEditing ? (
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {models.length === 0 && <span style={{ color: '#aaa', fontSize: '13px' }}>Chưa có model</span>}
                                                            {models.map((m, i) => (
                                                                <div key={i} className="ai-model-badge">
                                                                    {m.name}
                                                                    {isRepo ? (
                                                                        <span className="model-id" style={{ color: '#e74c3c' }}>{m.repo}</span>
                                                                    ) : (
                                                                        <>
                                                                            <span className="model-id">{m.model}</span>
                                                                            {m.sizes && m.sizes.length > 0 && (
                                                                                <span className="model-id" style={{ marginLeft: 4, color: '#1abc9c' }}>
                                                                                    [{m.sizes.map(s => typeof s === 'object' ? `${s.param} (${formatSize(s.size)})` : s).join(', ')}]
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            {editModels.map((m, i) => (
                                                                <div key={i} style={{ marginBottom: hasSize ? 10 : 0 }}>
                                                                    <div className="edit-row">
                                                                        <Input bsSize="sm" value={m.name}
                                                                            onChange={e => updateAiModel(i, 'name', e.target.value)}
                                                                            placeholder="Tên hiển thị" style={{ flex: 1, minWidth: 0 }} />
                                                                        {isRepo ? (
                                                                            <Input bsSize="sm" value={m.repo || ''}
                                                                                onChange={e => updateAiModel(i, 'repo', e.target.value)}
                                                                                placeholder="org/model-name" style={{ flex: 2, minWidth: 0, fontFamily: 'monospace' }} />
                                                                        ) : (
                                                                            <Input bsSize="sm" value={m.model}
                                                                                onChange={e => updateAiModel(i, 'model', e.target.value)}
                                                                                placeholder="Model ID" style={{ flex: 1, minWidth: 0, fontFamily: 'monospace' }} />
                                                                        )}
                                                                        <button className="btn-icon-sm" style={{ background: '#f0f2f5' }}
                                                                            onClick={() => moveAiModel(i, -1)} disabled={i === 0} title="Lên">
                                                                            <i className="fas fa-arrow-up" style={{ fontSize: '10px' }}></i>
                                                                        </button>
                                                                        <button className="btn-icon-sm" style={{ background: '#f0f2f5' }}
                                                                            onClick={() => moveAiModel(i, 1)} disabled={i === editModels.length - 1} title="Xuống">
                                                                            <i className="fas fa-arrow-down" style={{ fontSize: '10px' }}></i>
                                                                        </button>
                                                                        <button className="btn-icon-sm" style={{ background: '#fee2e2', color: '#f5365c' }}
                                                                            onClick={() => removeAiModel(i)} title="Xóa">
                                                                            <i className="fas fa-trash" style={{ fontSize: '10px' }}></i>
                                                                        </button>
                                                                    </div>
                                                                    {hasSize && (
                                                                        <div style={{ marginLeft: 12, marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #1abc9c33' }}>
                                                                            {(m.sizes || []).map((s, si) => {
                                                                                const sizeObj = typeof s === 'object' ? s : { param: s, size: 0 };
                                                                                const sizeGB = sizeObj.size ? (sizeObj.size / 1073741824).toFixed(2) : '';
                                                                                return (
                                                                                    <div key={si} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 3 }}>
                                                                                        <Input bsSize="sm" value={sizeObj.param}
                                                                                            onChange={e => {
                                                                                                const newSizes = [...(m.sizes || [])];
                                                                                                newSizes[si] = { ...sizeObj, param: e.target.value };
                                                                                                updateAiModel(i, 'sizes', newSizes);
                                                                                            }}
                                                                                            placeholder="Param (1B)"
                                                                                            style={{ width: 70, fontFamily: 'monospace', color: '#1abc9c', flex: 'none' }} />
                                                                                        <Input bsSize="sm" value={sizeGB}
                                                                                            onChange={e => {
                                                                                                const gb = parseFloat(e.target.value) || 0;
                                                                                                const bytes = Math.round(gb * 1073741824);
                                                                                                const newSizes = [...(m.sizes || [])];
                                                                                                newSizes[si] = { ...sizeObj, size: bytes };
                                                                                                updateAiModel(i, 'sizes', newSizes);
                                                                                            }}
                                                                                            placeholder="GB"
                                                                                            style={{ width: 80, fontFamily: 'monospace', flex: 'none' }} />
                                                                                        <span style={{ fontSize: 11, color: '#94a3b8' }}>GB</span>
                                                                                        <button className="btn-icon-sm" style={{ background: '#fee2e2', color: '#f5365c', width: 22, height: 22 }}
                                                                                            onClick={() => {
                                                                                                const newSizes = (m.sizes || []).filter((_, idx) => idx !== si);
                                                                                                updateAiModel(i, 'sizes', newSizes);
                                                                                            }} title="Xóa size">
                                                                                            <i className="fas fa-minus" style={{ fontSize: '8px' }}></i>
                                                                                        </button>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                            <button className="btn-icon-sm" style={{
                                                                                background: '#e0f7fa', color: '#1abc9c', height: 22, borderRadius: 6, fontSize: 11, gap: 4, paddingLeft: 8, paddingRight: 8
                                                                            }} onClick={() => {
                                                                                const newSizes = [...(m.sizes || []), { param: '', size: 0 }];
                                                                                updateAiModel(i, 'sizes', newSizes);
                                                                            }}>
                                                                                <i className="fas fa-plus" style={{ fontSize: '8px' }}></i> Size
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            <button className="btn-icon-sm" style={{
                                                                background: '#e8f5e9', color: '#2dce89', width: '100%',
                                                                marginTop: '6px', height: '30px', borderRadius: '8px', fontSize: '13px', gap: '6px'
                                                            }} onClick={addAiModel}>
                                                                <i className="fas fa-plus"></i> Thêm model
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </CardBody>
            </Card>
        </div>
    );
}

export default DHBRenderModel;
